// src/pages/PainelChamados/ModalAtribuir.jsx
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronDown } from "lucide-react";
import { useState, useEffect, useRef } from "react";

const API_URL = process.env.REACT_APP_API_URL;

export default function ModalAtribuir({ open, onClose, chamadoId, tecnicoResponsavel, onSalvo }) {
  const [quemAtribui, setQuemAtribui] = useState("");
  const [novoTecnico, setNovoTecnico] = useState(null); // { id, nome }
  const [tecnicos, setTecnicos] = useState([]);
  const [openSelect, setOpenSelect] = useState(false);
  const refSelect = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (refSelect.current && !refSelect.current.contains(e.target)) setOpenSelect(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (tecnicoResponsavel) setQuemAtribui(tecnicoResponsavel);
  }, [tecnicoResponsavel, open]);

  useEffect(() => {
    const fetchTecnicos = async () => {
      try {
        const resp = await fetch(`${API_URL}/usuarios/listar.php`);
        const data = await resp.json();
        if (data.status === "success") setTecnicos(data.dados);
        else setTecnicos([]);
      } catch (err) {
        console.error("Erro ao carregar técnicos:", err);
        setTecnicos([]);
      }
    };
    if (open) fetchTecnicos();
  }, [open]);

  const handleConfirm = async () => {
    // validações rápidas e debug
    if (!quemAtribui) {
      alert("Campo 'Quem está atribuindo' vazio!");
      return;
    }
    if (!novoTecnico) {
      alert("Selecione o novo técnico responsável!");
      return;
    }
    if (!chamadoId) {
      // caso comum: o usuário está criando o chamado (ainda não existe id)
      alert(
        "Atribuição falhou porque o chamado ainda não tem ID. Salve o chamado primeiro (clique em Salvar ou Finalizar) e depois atribua, ou abra esse formulário a partir da lista (chamado já existente)."
      );
      console.warn("Payload abortado (chamadoId ausente).", {
        chamadoId,
        tecnico_atribuido_por: quemAtribui,
        novo_tecnico_responsavel: novoTecnico,
      });
      return;
    }

    const payload = {
      id_chamado: chamadoId,
      tecnico_atribuido_por: quemAtribui,
      novo_tecnico_responsavel: novoTecnico.id,
    };

    console.log("Enviando atribuição ->", payload);

    try {
      const resp = await fetch(`${API_URL}/chamados/atribuir.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await resp.json();
      console.log("Resposta atribuir.php:", data);

      if (data.sucesso) {
        alert("Chamado atribuído com sucesso!");
        setNovoTecnico(null);
        onClose();
        if (onSalvo) onSalvo();
      } else {
        // mostra debug retornado (se houver)
        alert("Erro ao atribuir chamado: " + (data.erro || "erro desconhecido"));
        if (data.debug) console.warn("Debug do servidor:", data.debug);
      }
    } catch (err) {
      console.error("Erro:", err);
      alert("Falha na comunicação com o servidor.");
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }} transition={{ duration: 0.2 }}
            className="bg-white rounded-lg shadow-lg w-[400px] p-6 relative"
          >
            <button onClick={onClose} className="absolute top-3 right-3 text-gray-500 hover:text-black">
              <X size={20} />
            </button>

            <h2 className="text-lg font-semibold mb-6 text-gray-800 text-center">Atribuir Chamado</h2>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-600 mb-1">Quem está atribuindo</label>
              <input
                type="text"
                value={quemAtribui}
                readOnly
                className="w-full border-b border-gray-400 px-1 py-2 outline-none bg-transparent text-gray-800 font-semibold"
              />
            </div>

            <div className="mb-8 relative" ref={refSelect}>
              <label className="block text-sm font-medium text-gray-600 mb-1">Novo técnico responsável</label>
              <div className="border-b border-gray-400 py-2 px-1 flex items-center justify-between cursor-pointer"
                   onClick={() => setOpenSelect(p => !p)}>
                <span className={novoTecnico ? "text-gray-800" : "text-gray-400"}>
                  {novoTecnico ? novoTecnico.nome : "Selecione um técnico..."}
                </span>
                <ChevronDown size={16} className={`transition-transform ${openSelect ? "rotate-180" : "rotate-0"}`} />
              </div>

              <AnimatePresence>
                {openSelect && (
                  <motion.ul
                    initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -5 }}
                    transition={{ duration: 0.2 }}
                    className="absolute left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-md z-10 max-h-52 overflow-y-auto"
                  >
                    {tecnicos.length > 0 ? (
                      tecnicos.map(t => (
                        <li key={t.id} onClick={() => { setNovoTecnico({ id: t.id, nome: t.nome }); setOpenSelect(false); }}
                            className="px-3 py-2 text-sm text-gray-700 hover:bg-blue-100 cursor-pointer">
                          {t.nome}
                        </li>
                      ))
                    ) : (
                      <li className="px-3 py-2 text-sm text-gray-500">Nenhum técnico encontrado</li>
                    )}
                  </motion.ul>
                )}
              </AnimatePresence>
            </div>

            <div className="flex justify-end gap-3">
              <button onClick={onClose} className="px-4 py-2 rounded-md bg-gray-200 hover:bg-gray-300 text-gray-800">Cancelar</button>
              <button onClick={handleConfirm} className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white font-semibold">Confirmar</button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
